/**
 * The content path prefix a team's pages live under. Teams without a slug keep the original
 * `tpp/{teamId}` prefix.
 *
 * Single source of truth for the convention: the backend derives the stored `content.path`
 * from this, and both frontends use it to display and link to those pages. Structurally typed
 * so it accepts a portal DTO, an admin DTO or a raw teams row.
 */
export function teamContentPrefix(team: { id: string; slug: string | null }): string {
  return team.slug ? `/${team.slug}` : `/tpp/${team.id}`;
}

/**
 * The Developer Portal team's own content pages (client-creation guides and the like) are
 * fetched by the frontend's help panels under this prefix. The team must keep this team URL;
 * changing or clearing it blanks those panels until this constant is updated and redeployed.
 */
export const DEVELOPER_PORTAL_TEAM_SLUG = 'developer-portal';
export const DEVELOPER_PORTAL_CONTENT_PREFIX = teamContentPrefix({ id: '', slug: DEVELOPER_PORTAL_TEAM_SLUG });

/** Width of `content.path` and `content.parent`. See migration 257. */
export const CONTENT_PATH_MAX_LENGTH = 255;

/**
 * Longest prefix teamContentPrefix can return, over both of its shapes.
 *
 * Derived rather than written as 41 so that raising the slug cap moves the budget below with
 * it. A hardcoded 41 would silently under-reserve the moment the cap changed, which is exactly
 * the trap a fixed title limit falls into.
 */
const TEAM_SLUG_MAX_LENGTH = 40; // keep in sync with TEAM_SLUG_REGEX in shared/portal/TeamDto.ts
const UUID_LENGTH = 36;
export const MAX_TEAM_CONTENT_PREFIX_LENGTH = Math.max('/'.length + TEAM_SLUG_MAX_LENGTH, '/tpp/'.length + UUID_LENGTH);

/**
 * How many characters a team page's path may use below its team prefix.
 *
 * Measured against the LONGEST possible prefix, not the team's current one, because a prefix
 * changes length underneath existing rows: claiming a slug shortens every path, clearing one
 * (and the rollback in migration 257) lengthens them all by up to 38 characters at once. Bound
 * the suffix once against the worst case and every stored path fits `/{slug}` and
 * `/tpp/{uuid}` alike, so a slug change can never overflow varchar(255) and needs no check of
 * its own.
 *
 * The cost is deliberate: a team on a 2-character slug forgoes 38 of the 252 characters its own
 * prefix would allow. That is the price of "clearing your slug can never fail" — do not
 * "optimise" this into a check against the current prefix, which would let a team write itself
 * into a state it cannot leave.
 */
export const TEAM_CONTENT_PATH_BUDGET = CONTENT_PATH_MAX_LENGTH - MAX_TEAM_CONTENT_PREFIX_LENGTH;

/**
 * The part of a team page's path that counts against TEAM_CONTENT_PATH_BUDGET.
 *
 * Falls back to the whole path when it does not sit under the team's current prefix. A stored
 * path can be out of step with it — the advanced admin content form writes paths directly, and
 * the row is only ever checked for team ownership, not for its location — and blindly slicing
 * off `prefix.length` characters would then under-count the suffix and admit a path that
 * overflows. Over-counting rejects instead, which is the safe direction to be wrong in.
 *
 * Matched at a segment boundary for the same reason pathPrefixCondition in contentDbProvider is:
 * a bare `startsWith` also matches a sibling that merely shares the leading characters, so team
 * `identity` would claim `/identity-core/...` and slice 9 characters it does not own.
 */
export function teamContentPathSuffix(path: string, team: { id: string; slug: string | null }): string {
  const prefix = teamContentPrefix(team);

  if (path === prefix) {
    return '';
  }

  return path.startsWith(`${prefix}/`) ? path.slice(prefix.length) : path;
}

/**
 * Shape of a single segment of a team page's content path.
 *
 * Stored paths are used as SQL `LIKE`/`starts_with` prefixes and, historically, as
 * `regexp_replace` patterns, so a segment must not contain characters with meaning in either.
 * Restricting to lowercase alphanumerics separated by single hyphens keeps every stored path
 * inert as both a pattern and a URL. Deliberately the same character shape as TEAM_SLUG_REGEX,
 * so one rule governs a content path from its team prefix down to its last segment. The length
 * bound differs: team slugs cap at 40 here, while a page segment is bounded by the title length
 * limit in teamPageSchema and, together with its ancestors, by TEAM_CONTENT_PATH_BUDGET.
 */
export const PAGE_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
